import React, { useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { config } from '../../config';
import { togglePopover } from '../../redux/Slices/popoverSlice';
import { showSuccessWithUndo } from '../../utils/snackbarHelpers';

import './scss/DeleteAction.scss';
import { textTransform } from '../../utils/helpers';
import { deleteCommentOptimistically, deleteReplyOptimistically, fetchActivities } from '../../redux/Slices/activitiesSlice';
import { deleteAttachment } from '../../redux/Slices/attachmentSlice';
import { deleteChecklist } from '../../redux/Slices/checklistSlice';
import { cardData } from '../../redux/Slices/cardSlice';
import { fetchMembers, optimisticRemoveMember } from '../../redux/Slices/memberSlice';

const DeleteAction = (props) => {
    const inputRef = useRef(null);
    const { card } = useSelector((state) => state.card);
    const { user } = useSelector((state) => state.user);

    const [loading, setLoading] = useState(false); // 👈 new loading state

    const boardId = card?.board;
    const { type, id, url, cardId, listId, attachmentId, apiPath, typeId } = props;

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const getWarningMessage = (type) => {
        switch (type) {
            case 'CARD':
                return "Deleting this card will remove all actions from the activity feed, and you cannot re-open it. This action cannot be undone.";
            case 'ACTIONLIST':
                return "Deleting this action list will permanently remove all associated cards. This action cannot be undone.";
            case 'COMMENT':
                return "Deleting this comment cannot be undone.";
            case 'REPLY':
                return "Deleting this reply cannot be undone.";
            case 'CHECKLIST':
                return "Deleting this checklist cannot be undone.";
            case 'ATTACHMENT':
                return "Deleting this attachment cannot be undone.";
            case 'MEMBER':
                return "This member will be removed from all cards on this board.";
            default:
                return "This action cannot be undone.";
        }
    };

    const handleDeleteAction = async () => {
        setLoading(true); // start loading
        try {
            let response;
            switch (type) {
                case 'CARD':
                    response = await fetch(url, {
                        method: 'DELETE',
                        credentials: 'include',
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                        },
                    });
                    break;
                case 'ACTIONLIST':
                    response = await fetch(config.API_URI + `/v1/actionLists/${listId}`, {
                        method: 'DELETE',
                        credentials: 'include',
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ boardId: id }),
                    });
                    break;
                case 'COMMENT':
                    response = await fetch(url, {
                        method: 'DELETE',
                        credentials: 'include',
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ activityId: id }),
                    });
                    if (!response.ok) {
                        dispatch(fetchActivities({ cardId }));
                    }
                    break;
                case 'REPLY':
                    response = await fetch(url, {
                        method: 'DELETE',
                        credentials: 'include',
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({}),
                    });
                    if (!response.ok) {
                        dispatch(fetchActivities({ cardId }));
                    }
                    break;
                case 'CHECKLIST':
                    const checklistResult = await dispatch(deleteChecklist({ cardId, checklistId: id }));
                    if (deleteChecklist.rejected.match(checklistResult)) {
                        throw new Error('Failed to delete checklist');
                    }
                    break;
                case 'ATTACHMENT':
                    const attachmentResult = await dispatch(deleteAttachment({ cardId, attachmentId: id }));
                    if (deleteAttachment.rejected.match(attachmentResult)) {
                        throw new Error('Failed to delete attachment');
                    }
                    if (card.cover.attachmentId === id) {
                        dispatch(cardData({
                            ...card,
                            cover: {}
                        }));
                    }
                    break;
                case 'MEMBER':
                    response = await fetch(url, {
                        method: 'DELETE',
                        credentials: 'include',
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({ memberId: id }),
                    });

                    // Check if the current user is leaving (removing themselves)
                    const isLeavingWorkspace = user?.user?._id === id && apiPath === 'workspace';
                    const isLeavingBoard = user?.user?._id === id && apiPath === 'board';

                    if (response.ok) {
                        if (isLeavingWorkspace) {
                            // Redirect to home page when leaving workspace
                            dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));
                            navigate('/');
                            return; // Exit early to prevent further processing
                        } else if (isLeavingBoard) {
                            // Redirect to workspace boards page when leaving board
                            dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));
                            // Extract workspace ID from the board URL if available
                            const workspaceMatch = window.location.pathname.match(/\/w\/([^\/]+)/);
                            if (workspaceMatch) {
                                navigate(`/w/${workspaceMatch[1]}/boards`);
                            } else {
                                navigate('/');
                            }
                            return; // Exit early to prevent further processing
                        }

                        // Refresh member lists after successful removal
                        if (apiPath === 'workspace') {
                            dispatch(fetchMembers({ type: 'workspace', id: typeId }));
                            dispatch(fetchMembers({ type: 'guests', id: typeId }));
                        } else {
                            dispatch(fetchMembers({ type: apiPath, id: typeId }));
                        }
                    }
                    break;
                default:
                    throw new Error('Unsupported delete type');
            }

            // Handle success for different types
            let deleteSuccessful = false;

            if (type === 'CHECKLIST' || type === 'ATTACHMENT') {
                // These types use async thunks and are already handled above
                deleteSuccessful = true;
            } else if (response && response.ok) {
                // These types use direct fetch calls
                deleteSuccessful = true;

                // Only update UI for types that use optimistic updates after server response
                if (type === 'REPLY') {
                    dispatch(deleteReplyOptimistically(id));
                } else if (type === 'COMMENT') {
                    dispatch(deleteCommentOptimistically(id));
                }
            } else {
                throw new Error('Failed to delete');
            }

            if (deleteSuccessful) {
                const undoActionType = `DELETE_${type}`;
                const undoData = {
                    id: id || cardId || listId || attachmentId,
                    type,
                    boardId,
                    cardId,
                    listId,
                    url,
                    apiPath
                };

                showSuccessWithUndo(
                    dispatch,
                    `${textTransform(type, 'capitalize')} deleted successfully!`,
                    undoActionType,
                    undoData
                );

                dispatch(togglePopover({ contentId: null, position: { top: 0, left: 0 }, targetId: null }));
            }
        } catch (error) {
            console.error('Error deleting the item:', error);
        } finally {
            setLoading(false); // stop loading
        }
    };

    return (
        <div className="delete-action">
            <h2>{type === 'MEMBER' ? `Remove ${textTransform(type, 'lowercase')}` : `Delete ${textTransform(type, 'lowercase')}`}?</h2>

            <div className="group">
                <p className="warning-message">{getWarningMessage(type)}</p>
                <button onClick={handleDeleteAction} disabled={loading}>
                    {loading
                        ? (type === 'MEMBER' ? 'Removing...' : 'Deleting...')
                        : (type === 'MEMBER' ? 'Remove' : 'Delete')}
                </button>

            </div>
        </div>
    );
};

export default DeleteAction;
